import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DownloadSimple, FloppyDisk, Trash, CaretUp, CaretDown, PaperPlaneTilt, Star } from "@phosphor-icons/react";
import LeadFilters from "./LeadFilters";
import LeadDetail from "./LeadDetail";
import { STATUS_LABEL, CONF_LABEL, HIRING_LABEL, TREND_LABEL, TRANCHES, ScorePill, ProcBadge, fmtDate } from "./meta";

const EXPORT_COLS = { name: "Société", siren: "SIREN", website: "Site web", departement: "Département", region: "Région", naf: "Code NAF",
  tranche: "Effectif", procedure: "Procédure vérifiée", hiring: "Recrutement observable", score: "Score", confidence: "Confiance",
  contacts: "Coordonnées autorisées", status: "Statut" };

const COLS = [["name", "Entreprise"], ["siren", "SIREN"], [null, "Région"], [null, "NAF"], ["tranche", "Effectif"], ["offers", "Offres 90 j"],
  [null, "Évolution"], [null, "Procédure"], ["event", "Dernier événement"], ["score", "Score"], [null, "Confiance"], [null, "Statut"], ["updated", "MAJ"]];

async function downloadCsv(payload) {
  const res = await api.post("/admin/leads/export", payload, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `interko-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function ExportDialog({ open, onClose, payload }) {
  const [cols, setCols] = useState(Object.keys(EXPORT_COLS));
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await downloadCsv({ ...payload, columns: cols }); toast.success("Export CSV téléchargé"); onClose(); }
    catch { toast.error("Export impossible"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-surface border-border" data-testid="lead-export-dialog">
        <DialogHeader><DialogTitle>Exporter en CSV (Excel / CRM)</DialogTitle></DialogHeader>
        <p className="text-xs text-muted">{payload.sirens ? `${payload.sirens.length} prospect(s) sélectionné(s)` : "Tous les prospects correspondant aux filtres"} · exclusions et oppositions respectées.</p>
        <div className="grid grid-cols-2 gap-2 my-2">
          {Object.entries(EXPORT_COLS).map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-fg">
              <Checkbox checked={cols.includes(k)} onCheckedChange={(v) => setCols(v ? [...cols, k] : cols.filter((x) => x !== k))} data-testid={`lead-export-col-${k}`} /> {l}
            </label>
          ))}
        </div>
        <button onClick={go} disabled={busy || !cols.length} data-testid="lead-export-confirm"
          className="bg-accent text-black font-semibold rounded-md py-2.5 text-sm hover-lift disabled:opacity-60">{busy ? "Export…" : "Télécharger le CSV"}</button>
      </DialogContent>
    </Dialog>
  );
}

function SegmentsBar({ filters, setFilters }) {
  const [segs, setSegs] = useState([]);
  const load = () => api.get("/admin/leads/segments").then(({ data }) => setSegs(data.segments)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    const name = window.prompt("Nom du segment (ex. « ESN 10–100 salariés, faible recrutement »)");
    if (!name) return;
    try { await api.post("/admin/leads/segments", { name, filters }); toast.success("Segment enregistré"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/leads/segments/${id}`); load(); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="lead-segments">
      <span className="overline mr-1">Segments</span>
      {segs.map((s) => (
        <span key={s.id} className="flex items-center gap-1 text-xs border border-border rounded-full pl-3 pr-1 py-1 bg-surface">
          <button onClick={() => setFilters(s.filters)} className="text-fg hover:text-accent" data-testid={`lead-segment-${s.id}`}>{s.name}</button>
          <button onClick={() => del(s.id)} className="text-muted hover:text-destructive p-0.5" data-testid={`lead-segment-delete-${s.id}`}><Trash size={11} /></button>
        </span>
      ))}
      <button onClick={save} className="flex items-center gap-1 text-xs text-accent border border-accent/40 rounded-full px-3 py-1 hover:bg-accent/10" data-testid="lead-segment-save">
        <FloppyDisk size={12} /> Enregistrer ce segment
      </button>
    </div>
  );
}

function BulkBar({ selected, clear, reload, onCampaign }) {
  const run = async (action, value) => {
    try { await api.post("/admin/leads/bulk", { sirens: selected, action, value }); toast.success("Mise à jour effectuée"); clear(); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 surface rounded-md px-3 py-2 border-accent/40" data-testid="lead-bulk-bar">
      <span className="text-xs text-accent font-semibold">{selected.length} sélectionné(s)</span>
      <select className="bg-surface-2 border border-border rounded text-xs px-2 py-1" defaultValue="" data-testid="lead-bulk-status"
        onChange={(e) => e.target.value && run("status", e.target.value)}>
        <option value="">Changer le statut…</option>{Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <button onClick={() => run("priority", true)} className="text-xs border border-border rounded px-2 py-1 flex items-center gap-1" data-testid="lead-bulk-priority"><Star size={12} /> Prioritaire</button>
      <button onClick={() => { const l = window.prompt("Nom de la liste"); if (l) run("list", l); }} className="text-xs border border-border rounded px-2 py-1" data-testid="lead-bulk-list">Ajouter à une liste</button>
      <button onClick={onCampaign} className="text-xs border border-accent/40 text-accent rounded px-2 py-1 flex items-center gap-1" data-testid="lead-bulk-campaign"><PaperPlaneTilt size={12} /> Préparer une campagne</button>
      <button onClick={() => window.confirm("Exclure ces entreprises de la prospection ?") && run("exclude")} className="text-xs border border-destructive/40 text-destructive rounded px-2 py-1" data-testid="lead-bulk-exclude">Exclure</button>
      <button onClick={clear} className="text-xs text-muted ml-auto">Désélectionner</button>
    </div>
  );
}

export default function LeadProspects({ meta, onCampaignCreated }) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ by: "score", order: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [lists, setLists] = useState([]);
  const [owners, setOwners] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, page_size: 25, sort: sort.by, order: sort.order };
      const { data: d } = await api.get("/admin/leads/companies", { params });
      setData(d);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  }, [filters, page, sort]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [filters, sort]);
  useEffect(() => {
    api.get("/admin/leads/lists").then(({ data: d }) => setLists(d.lists)).catch(() => {});
    api.get("/admin/leads/owners").then(({ data: d }) => setOwners(d.owners)).catch(() => {});
  }, []);

  const toggleSort = (by) => by && setSort((s) => ({ by, order: s.by === by && s.order === "desc" ? "asc" : "desc" }));
  const allOnPage = data.items.map((i) => i.siren);
  const pages = Math.max(1, Math.ceil(data.total / 25));

  const createCampaign = async () => {
    try {
      const { data: d } = await api.post("/admin/leads/campaigns", { sirens: selected });
      toast.success("Campagne préparée (brouillon) — vérifiez-la avant tout envoi.");
      setSelected([]); onCampaignCreated(d.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="lead-prospects">
      <LeadFilters value={filters} onChange={setFilters} meta={meta} lists={lists} owners={owners} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentsBar filters={filters} setFilters={setFilters} />
        <button onClick={() => setExportOpen(true)} className="flex items-center gap-1.5 text-xs border border-border rounded-md px-3 py-1.5 hover-lift" data-testid="lead-export-btn">
          <DownloadSimple size={13} /> Export CSV
        </button>
      </div>
      {selected.length > 0 && <BulkBar selected={selected} clear={() => setSelected([])} reload={load} onCampaign={createCampaign} />}

      <div className="surface rounded-lg overflow-x-auto" data-testid="lead-table">
        <table className="w-full text-xs">
          <thead className="bg-surface-2/60 text-muted">
            <tr>
              <th className="p-2 w-8"><Checkbox data-testid="lead-select-all" checked={allOnPage.length > 0 && allOnPage.every((s) => selected.includes(s))}
                onCheckedChange={(v) => setSelected(v ? [...new Set([...selected, ...allOnPage])] : selected.filter((s) => !allOnPage.includes(s)))} /></th>
              {COLS.map(([k, l]) => (
                <th key={l} onClick={() => toggleSort(k)} className={`text-left p-2 font-medium whitespace-nowrap ${k ? "cursor-pointer hover:text-fg" : ""}`} data-testid={k ? `lead-sort-${k}` : undefined}>
                  {l}{sort.by === k && (sort.order === "desc" ? <CaretDown size={10} className="inline ml-0.5" /> : <CaretUp size={10} className="inline ml-0.5" />)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={loading ? "opacity-60" : ""}>
            {data.items.map((c) => (
              <tr key={c.siren} className="border-t border-border/60 hover:bg-surface-2/40 cursor-pointer" onClick={() => setDetail(c.siren)} data-testid={`lead-row-${c.siren}`}>
                <td className="p-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.includes(c.siren)} onCheckedChange={(v) => setSelected(v ? [...selected, c.siren] : selected.filter((s) => s !== c.siren))} data-testid={`lead-select-${c.siren}`} />
                </td>
                <td className="p-2 text-fg font-medium max-w-[220px] truncate">{c.priority && <Star size={11} weight="fill" className="inline text-amber-400 mr-1" />}{c.name}{c.etat_administratif === "C" && <span className="text-destructive ml-1">(fermée)</span>}</td>
                <td className="p-2 font-mono text-muted">{c.siren}</td>
                <td className="p-2 text-muted">{c.region || "—"}{c.departement ? ` (${c.departement})` : ""}</td>
                <td className="p-2 text-muted font-mono" title={c.naf_label}>{c.naf}</td>
                <td className="p-2 text-muted whitespace-nowrap">{TRANCHES[c.tranche_effectif] || c.tranche_label}</td>
                <td className="p-2 text-fg">{c.hiring?.offers_90d ?? <span className="text-muted" title="Données de recrutement insuffisantes">insuff.</span>}</td>
                <td className="p-2 text-muted whitespace-nowrap">{TREND_LABEL[c.hiring?.trend] || HIRING_LABEL[c.hiring?.level] || "—"}</td>
                <td className="p-2"><ProcBadge legal={c.legal} /></td>
                <td className="p-2 text-muted whitespace-nowrap">{fmtDate(c.legal?.last_event_date)}</td>
                <td className="p-2"><ScorePill score={c.score_effective} cls={c.classification_effective} testid={`lead-score-${c.siren}`} /></td>
                <td className="p-2 text-muted">{CONF_LABEL[c.score?.confidence] || "—"}</td>
                <td className="p-2 text-fg whitespace-nowrap">{STATUS_LABEL[c.prospect_status] || c.prospect_status}</td>
                <td className="p-2 text-muted whitespace-nowrap">{fmtDate(c.updated_at)}</td>
              </tr>
            ))}
            {!data.items.length && !loading && <tr><td colSpan={14} className="p-8 text-center text-muted" data-testid="lead-empty">Aucune entreprise ne correspond à ces filtres.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-muted" data-testid="lead-pagination">
        <span data-testid="lead-total">{data.total.toLocaleString("fr-FR")} entreprise(s)</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border border-border rounded px-2 py-1 disabled:opacity-40" data-testid="lead-page-prev">Précédent</button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="border border-border rounded px-2 py-1 disabled:opacity-40" data-testid="lead-page-next">Suivant</button>
        </div>
      </div>
      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} payload={selected.length ? { sirens: selected } : { filters }} />
      <LeadDetail siren={detail} onClose={() => setDetail(null)} onChanged={load} owners={owners} />
    </div>
  );
}
